import { Formik } from "formik";
import { isEmpty } from "lodash-es";
import {
  Row,
  Column,
  Form,
  BadRequestError,
  CancelButton,
  SubmitButton,
  TextInput,
  TextArea,
  useApi,
  useNotifier,
  url,
} from "lcm-iot-commons";

import Loader from "lcm-iot-commons/client/lib/components/Loader";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

type OwnProps = {
  isLoadingTenant?: boolean;
  edit?: boolean;
  tenant?: {
    id?: number;
    name?: string;
    description?: string;
  };
};

const defaultProps: Partial<OwnProps> = {
  isLoadingTenant: false,
  edit: false,
  tenant: undefined,
};

type Props = OwnProps & typeof defaultProps;

export default function TenantForm({ isLoadingTenant, edit, tenant }: Props) {
  const api = useApi();
  const intl = useIntl();
  const notifier = useNotifier();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { mutateAsync: updateTenant } = api.patch.useMutation(
    `/tenants/${id}`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: createTenant } = api.post.useMutation("/tenants", {
    onError: () => {},
  });

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      if (edit) {
        await updateTenant({
          name: values.name,
          description: values.description,
        });
        navigate(url(`/tenants/${(tenant as $TSFixMe).id}`));
      } else {
        const response = await createTenant({
          name: values.name,
          description: values.description,
          public: false,
        });
        navigate(url(`/tenants/${(response as $TSFixMe).id}`));
      }
      notifier.showSuccess(
        intl.formatMessage({
          id: "tenant_edit.success_notification",
        }),
      );
    } catch (error) {
      const formErrors = {};
      if (error instanceof BadRequestError && error.contains("taken")) {
        (formErrors as $TSFixMe).name = intl.formatMessage({
          id: "api.error.tenant.taken",
        });
      } else {
        notifier.showError(intl.formatMessage({ id: "api.error.unknown" }));
      }
      actions.setErrors(formErrors);
      actions.setSubmitting(false);
    }
  };

  const validate = (values: $TSFixMe) => {
    const errors = {};
    if (isEmpty(values.name?.trim())) {
      (errors as $TSFixMe).name = intl.formatMessage({
        id: "validation.name.mandatory",
      });
    }
    if (values.name?.length > 60) {
      (errors as $TSFixMe).name = intl.formatMessage(
        { id: "validation.name.too_long" },
        { characters: 60 },
      );
    }
    return errors;
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting } = formProps;
    return (
      <Form {...formProps}>
        <Row>
          <Column>
            <TextInput
              {...formProps}
              id="tenant-name"
              name="name"
              label={(intl as $TSFixMe).formatMessage({ id: "label.name" })}
              required
            />
            <TextArea
              {...formProps}
              id="tenant-description"
              name="description"
              label={(intl as $TSFixMe).formatMessage({
                id: "label.description",
              })}
            />
          </Column>
        </Row>
        <div className="btn-group">
          <SubmitButton
            id="tenant-form-submit"
            fetching={isSubmitting}
            disabled={!formProps.dirty}
          />
          {/* @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: any; }' is not assig... Remove this comment to see the full error message */}
          <CancelButton id="tenant-form-cancel" disabled={isSubmitting} />
        </div>
      </Form>
    );
  };

  return (
    <>
      {!isLoadingTenant && (
        <Formik
          validate={validate}
          onSubmit={onSubmit}
          initialValues={{
            name: (tenant as $TSFixMe)?.name,
            description: (tenant as $TSFixMe)?.description,
          }}
        >
          {renderForm}
        </Formik>
      )}
      <Loader loading={isLoadingTenant} />
    </>
  );
}

TenantForm.defaultProps = defaultProps;
